import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { PATHWAYS_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { CustomerName } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customer'
import {
  COMMON_MIXPANEL_EVENTS,
  COMMON_MIXPANEL_PAGE,
  COMMON_MIXPANEL_PROPERTIES,
  ESSENTIALS_CONTENT_EVENTS_PROPERTIES,
  PATHWAYS_ENTRY_POINTS,
  PATHWAYS_EVENTS,
  PATHWAYS_OPTIONS,
  PATHWAYS_PAGE,
  PATHWAYS_PROPERTIES,
  WELLNESS_CHECK_IN_ENTRY_POINTS,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { PAGE_ROUTES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { getHelpEmailAddress } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/commonUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { useLyraIntl } from '@lyrahealth-inc/shared-app-logic/src/features/intl/hooks/useLyraIntl'
import { PathwayOptions } from '@lyrahealth-inc/shared-app-logic/src/features/pathways/constants'
import { useGetPathwaysVersion } from '@lyrahealth-inc/shared-app-logic/src/features/pathways/hooks/useGetPathwaysVersion'
import { getPathwayTileMessages } from '@lyrahealth-inc/shared-app-logic/src/features/pathways/messages'
import { DirectLinkSource } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { PathwaysMicrositeHomePageProps } from '@lyrahealth-inc/ui-core-crossplatform/src/microsite/pages/pathwaysMicrositeHomePage/PathwaysMicrositeHomePage'
import { openUrl } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/expo-linking'
import { mailTo } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { useCustomerCostProperties } from './useCustomerCostProperties'
import { useIsCostMessagingEnabled } from './useIsCostMessagingEnabled'
import { useIsMultiCtaPathwaysAvailable } from './useIsMultiCtaPathwaysAvailable'
import { useMicrositeCareOfferingDescriptionType } from './useMicrositeCareOfferingDescriptionType'
import { useNavigateToEssentialsContent } from './useNavigateToEssentialsContent'
import { useNavigateToWellnessCheckIn } from './useNavigateToWellnessCheckIn'
import { useWellnessCheckIn } from './useWellnessCheckIn'
import { getShouldShowLiveChat } from '../../data/appGlobals/appGlobalsSelectors'
import {
  getBeforeLaunch,
  getCustomerInfo,
  getCustomerLaunchDate,
  getCustomerName,
  getCustomerPropertiesMap,
  getDisplayVisitsPerIssuePerYear,
  getDisplayVisitsPerIssuePerYearByProgram,
  getHideDependentField,
  getIsCoachingEnabled,
  getIsCustomerLyra,
  getIsTherapyEnabled,
  getLegalNotice,
  getLibraryDisabled,
  getNumberOfSessions,
  getNumberOfSessionsByProgram,
  getProgramMembershipType,
} from '../../data/customer/customerSelectors'
import { MIXPANEL_EVENTS } from '../../data/mixpanel'
import { TrackEventWithObjAction } from '../../data/mixpanel/types'
import { useAppDispatch } from '../../data/storeConfiguration/store'
import {
  getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer,
  getIsUserLoggedIn,
  getUser,
} from '../../data/user/userSelectors'
import { setDirectLinkSource } from '../../features/onboard/data/onboardDataActions'
import { setSelectedPathwayOption } from '../../features/register/data/registerActions'
import { doesUserHaveMembershipInCustomer } from '../utils/customerUtils'
import { rerouteFromMicrosite } from '../utils/micrositeUtils'

export const useMicrositeSectionProps = ({
  trackEventWithObj,
  isAutoEnrollmentVariant = false,
  isComingSoon = false,
}: {
  trackEventWithObj: TrackEventWithObjAction
  isAutoEnrollmentVariant?: boolean
  isComingSoon?: boolean
}) => {
  const { isPathwaysMicrositeMultiCTAEnabled, isMicrositeLookAndFeelUpdateEnabled } = useFlags()
  const membershipType = useSelector(getProgramMembershipType)
  const isLyrian = useSelector(getIsCustomerLyra)
  const isCoachingEnabled = useSelector(getIsCoachingEnabled)
  const isTherapyEnabled = useSelector(getIsTherapyEnabled)
  const isUserAssumedInternational = useSelector(getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer)
  const user = useSelector(getUser)
  const beforeLaunch = useSelector(getBeforeLaunch)
  const startDate = useSelector(getCustomerLaunchDate)
  const numberOfSessions = useSelector(getNumberOfSessions)
  const numberOfSessionsByProgram = useSelector(getNumberOfSessionsByProgram)
  const displayVisitsPerIssuePerYearByProgram = useSelector(getDisplayVisitsPerIssuePerYearByProgram)
  const customerDisplayName = useSelector(getCustomerInfo)
  const customerName = useSelector(getCustomerName)
  const showLiveChat = useSelector(getShouldShowLiveChat)
  const legalNotice = useSelector(getLegalNotice)
  const isDependentDisabled = useSelector(getHideDependentField)
  const libraryDisabled = useSelector(getLibraryDisabled)
  const displayVisitsPerIssuePerYear = useSelector(getDisplayVisitsPerIssuePerYear)
  const customerPropertiesMap = useSelector(getCustomerPropertiesMap)?.toJS()

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { activeLanguage } = useLyraIntl()
  const { showMultiCTAPathways } = useIsMultiCtaPathwaysAvailable()

  const pathwaysType = isComingSoon
    ? PATHWAYS_TYPE.COMING_SOON
    : (isPathwaysMicrositeMultiCTAEnabled && showMultiCTAPathways) || isAutoEnrollmentVariant
    ? PATHWAYS_TYPE.MULTIPLE
    : PATHWAYS_TYPE.SINGLE
  const careOfferingDescriptionType = useMicrositeCareOfferingDescriptionType(pathwaysType)

  const customerCostProperties = useCustomerCostProperties()
  const isCostMessagingEnabled = useIsCostMessagingEnabled()
  const { showWellnessCheckIn } = useWellnessCheckIn()
  const { navigateToWellnessCheckInForm } = useNavigateToWellnessCheckIn()
  const navigateToEssentialsContent = useNavigateToEssentialsContent()
  const isLoggedIn = useSelector(getIsUserLoggedIn)
  const pathwaysVersion = useGetPathwaysVersion()
  const pathwayTileMessages = getPathwayTileMessages({
    coachingEnabled: isCoachingEnabled,
    therapyEnabled: isTherapyEnabled,
    internationalUser: isUserAssumedInternational,
  })

  const hasMembershipInCustomer = doesUserHaveMembershipInCustomer({ membershipType })

  const [startChat, setStartChat] = useState(false)

  const micrositeSectionProps: Omit<PathwaysMicrositeHomePageProps, 'micrositeConfig'> = useMemo(() => {
    const onPressLiveChat = () => {
      setStartChat(!startChat)
    }

    const contactEmail = getHelpEmailAddress({
      customerName,
      isInternationalUser: isUserAssumedInternational,
    })

    const onPressEmail = () => {
      trackEventWithObj({
        event: COMMON_MIXPANEL_EVENTS.CLICK_EMAIL_CN,
        [COMMON_MIXPANEL_PROPERTIES.PAGE]: COMMON_MIXPANEL_PAGE.MICROSITE,
      })
      mailTo(contactEmail)
    }

    const onContactUsPress = () => {
      trackEventWithObj({
        event: COMMON_MIXPANEL_EVENTS.CLICK_PHONE_CN,
        [COMMON_MIXPANEL_PROPERTIES.PAGE]: COMMON_MIXPANEL_PAGE.MICROSITE,
      })
      return navigate('/care-team-phone-numbers')
    }

    const onLegalFooterLinkPress = (url: string) => {
      openUrl(url)
    }

    return {
      heroSectionProps: {
        pathwaysType,
        startDate,
        onCreateAccountButtonPress: () => {
          let navigationPath = PAGE_ROUTES.REGISTER

          // Enable Forked Triage for Amazon
          if (customerName === CustomerName.AMAZON) {
            navigationPath = PAGE_ROUTES.PATHWAYS.FORKED_TRIAGE
          }

          navigate(navigationPath, {
            state: { mixpanelEntryPoint: PATHWAYS_ENTRY_POINTS.MICROSITE },
          })
          trackEventWithObj({
            event: PATHWAYS_EVENTS.CLICK_CREATE_ACCOUNT,
            [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: PATHWAYS_ENTRY_POINTS.MICROSITE,
            ...pathwaysVersion,
          })
        },
        onExploreOnYourOwnCTAPress: () => {
          document
            .querySelector('[data-testid="HeroSection-pathwaysHomepage"]')
            ?.nextElementSibling?.scrollIntoView({ behavior: 'smooth' })
          trackEventWithObj({
            event: PATHWAYS_EVENTS.CLICK_EXPLORE_ON_YOUR_OWN,
            page: PATHWAYS_PAGE.CUSTOMER_MICROSITE,
            ...pathwaysVersion,
          })
        },
        onProviderLedCardPress: () => {
          dispatch(setDirectLinkSource(DirectLinkSource.MICROSITE_FORKED_TRIAGE))
          navigate(PAGE_ROUTES.PATHWAYS.FORKED_TRIAGE, {
            state: { mixpanelEntryPoint: PATHWAYS_ENTRY_POINTS.MICROSITE },
          })
        },
        onWellnessCheckinCardPress: showWellnessCheckIn
          ? () => navigateToWellnessCheckInForm({ entryPoint: WELLNESS_CHECK_IN_ENTRY_POINTS.MICROSITE })
          : undefined,
        onSelfCareCardPress: () => {
          navigate(PAGE_ROUTES.PATHWAYS.SELF_CARE, {
            state: { mixpanelEntryPoint: PATHWAYS_ENTRY_POINTS.MICROSITE },
          })
        },
        pathwayTileMessages,
        isMicrositeLookAndFeelUpdateEnabled,
      },
      faqSectionProps: {
        rerouteFromMicrosite: () => {
          rerouteFromMicrosite({
            user,
            navigate,
            trackEventWithObj,
            mixpanelEvent: MIXPANEL_EVENTS.FOOTER_CREATE_ACCOUNT,
            mixpanelEntryPoint: isComingSoon ? PATHWAYS_PAGE.COMING_SOON : PATHWAYS_ENTRY_POINTS.FAQS,
          })
        },
        membershipType,
        beforeLaunch,
        startDate,
        hasMembershipInCustomer,
        customerCostProperties,
        isCostMessagingEnabled,
        onReachOutToCareNavTeamLinkPress: () => {
          navigate('/care-team-phone-numbers')
        },
        onQuestionPress: (title: string) => {
          trackEventWithObj({
            event: PATHWAYS_EVENTS.CLICK_FAQS,
            page: isComingSoon ? PATHWAYS_PAGE.COMING_SOON : PATHWAYS_PAGE.CUSTOMER_MICROSITE,
            [PATHWAYS_PROPERTIES.FAQ_QUESTION]: title,
          })
        },
      },
      lyraOfferingsSectionProps: {
        customerPropertiesMap,
        pathwaysType,
        isInternational: isUserAssumedInternational,
        meetWithAnExpertTabPress: () => {
          trackEventWithObj({
            event: PATHWAYS_EVENTS.VIEW_MEET_WITH_AN_EXPERT_TAB,
            page: isComingSoon ? PATHWAYS_PAGE.COMING_SOON : PATHWAYS_PAGE.CUSTOMER_MICROSITE,
          })
        },
        wellnessResourcesTabPress: () => {
          trackEventWithObj({
            event: PATHWAYS_EVENTS.VIEW_CONTENT_LIBRARY_TAB,
            page: isComingSoon ? PATHWAYS_PAGE.COMING_SOON : PATHWAYS_PAGE.CUSTOMER_MICROSITE,
          })
        },
        lyraOfferingsTabPress: () => {
          trackEventWithObj({
            event: PATHWAYS_EVENTS.VIEW_ALL_OFFERINGS_TAB,
            page: isComingSoon ? PATHWAYS_PAGE.COMING_SOON : PATHWAYS_PAGE.CUSTOMER_MICROSITE,
          })
        },
        meetWithAnExpertCtaPress: () => {
          dispatch(setDirectLinkSource(DirectLinkSource.MICROSITE_BOOK_APPT_CTA))
          trackEventWithObj({
            event: PATHWAYS_EVENTS.CLICK_MEET_WITH_AN_EXPERT,
          })
          if (pathwaysType === PATHWAYS_TYPE.MULTIPLE) {
            navigate(PAGE_ROUTES.PATHWAYS.FORKED_TRIAGE, {
              state: { mixpanelEntryPoint: PATHWAYS_OPTIONS.EXPERT },
            })
          } else {
            navigate('/register/connect', {
              state: { mixpanelEntryPoint: PATHWAYS_OPTIONS.EXPERT },
            })
          }
        },
        wellnessResourcesCtaPress: () => {
          trackEventWithObj({
            event: PATHWAYS_EVENTS.CLICK_ACCESS_FULL_LIBRARY,
            category: 'lyra offerings',
          })
          if (pathwaysType === PATHWAYS_TYPE.MULTIPLE) {
            navigate(PAGE_ROUTES.PATHWAYS.SELF_CARE, {
              state: { mixpanelEntryPoint: PATHWAYS_OPTIONS.CONTENT_LIBRARY },
            })
          } else {
            navigate('/register/connect', {
              state: { mixpanelEntryPoint: PATHWAYS_OPTIONS.CONTENT_LIBRARY },
            })
          }
        },
        lyraOfferingsCtaPress: () => {
          trackEventWithObj({
            event: PATHWAYS_EVENTS.CLICK_CREATE_ACCOUNT,
            [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: PATHWAYS_ENTRY_POINTS.ALL_OFFERINGS,
          })
          navigate('/register/connect', {
            state: { mixpanelEntryPoint: PATHWAYS_ENTRY_POINTS.ALL_OFFERINGS },
          })
        },
      },
      selfCareSectionProps: {
        onCTAPress: () => {
          navigate(PAGE_ROUTES.PATHWAYS.ESSENTIALS_HOMEPAGE_WIDGET)
          dispatch(setSelectedPathwayOption(PathwayOptions.ESSENTIALS))
        },
        language: activeLanguage,
        onContentClicked: (data) => {
          trackEventWithObj({
            event: PATHWAYS_EVENTS.CLICK_SELF_CARE_RESOURCE,
            [COMMON_MIXPANEL_PROPERTIES.PAGE]: isComingSoon
              ? PATHWAYS_PAGE.COMING_SOON
              : PATHWAYS_PAGE.CUSTOMER_MICROSITE,
            [ESSENTIALS_CONTENT_EVENTS_PROPERTIES.ARTICLE_NAME]: data.title,
          })
          navigateToEssentialsContent({ slug: data.slug, newWindow: true, loggedIn: isLoggedIn })
        },
        pathwaysType: isComingSoon ? PATHWAYS_TYPE.COMING_SOON : undefined,
      },
      careNavigatorSectionProps: {
        onChatLivePress: onPressLiveChat,
        onPressEmail: onPressEmail,
        onContactUsPress: onContactUsPress,
        showLiveChat: showLiveChat || isUserAssumedInternational,
        isInternational: isUserAssumedInternational,
        contactEmail,
        isLyrian: isLyrian,
      },
      legalAndCopyrightFooterProps: {
        onFooterLinkPressed: onLegalFooterLinkPress,
        isInternational: isUserAssumedInternational,
        legalNotice,
      },
      membershipSectionProps: {
        customerDisplayName,
        numberOfSessions,
        numberOfSessionsByProgram,
        displayVisitsPerIssuePerYearByProgram,
        displayVisitsPerIssuePerYear,
        careOfferingDescriptionType,
        isLibraryDisabled: libraryDisabled,
        isDependentDisabled: isComingSoon ? undefined : isDependentDisabled,
      },
      mobileAppPromoSectionProps: { language: activeLanguage },
      mobileAppPromoBannerProps: {
        onGetAppLinkPress: () =>
          document.getElementById('lyra-mobile-app-promo-section')?.scrollIntoView({ behavior: 'smooth' }),
      },
      customerBrandedBannerProps: { customerName },
    }
  }, [
    customerName,
    isUserAssumedInternational,
    pathwaysType,
    showWellnessCheckIn,
    pathwayTileMessages,
    isMicrositeLookAndFeelUpdateEnabled,
    membershipType,
    beforeLaunch,
    startDate,
    hasMembershipInCustomer,
    customerCostProperties,
    isCostMessagingEnabled,
    customerPropertiesMap,
    activeLanguage,
    isComingSoon,
    showLiveChat,
    isLyrian,
    legalNotice,
    customerDisplayName,
    numberOfSessions,
    numberOfSessionsByProgram,
    displayVisitsPerIssuePerYearByProgram,
    displayVisitsPerIssuePerYear,
    careOfferingDescriptionType,
    libraryDisabled,
    isDependentDisabled,
    startChat,
    trackEventWithObj,
    navigate,
    pathwaysVersion,
    dispatch,
    navigateToWellnessCheckInForm,
    user,
    navigateToEssentialsContent,
    isLoggedIn,
  ])

  return { micrositeSectionProps, startChat }
}
